import {
  readLegacyBinding,
  requireLegacy,
  writeLegacyBinding,
  type LegacyReaderHandle,
  type LegacyReaderOpenOptions,
  type LegacySettingsSheet,
  type LegacySettingsState,
  type LegacyVerseModel,
  type LegacyWordToken,
} from '../../legacy/legacyGlobals';
import { maxMushafPage } from '../../shared/quran/dataSource';
import type { PageVerse } from '../../shared/quran/pageStructure';
import type { ReadingPreferences } from '../../shared/state/readingPreferences';

/** A verse as the reader renders it: the legacy row model plus its word chips. */
export interface VerseRowModel extends LegacyVerseModel {
  words: LegacyWordToken[] | null;
}

/** One entry of Quran.com's recitations list, as the legacy player keeps it. */
export interface Reciter {
  id: number;
  reciter_name?: string;
  style?: string;
}

/**
 * The application's one recitation channel: a single <audio> element and one
 * chosen reciter, shared with whatever else recites (the printed-page reader
 * still does through the legacy player).
 */
export interface RecitationAudio {
  element: HTMLAudioElement;
  /** False in the shared preview build, where recitation is switched off. */
  enabled(): boolean;
  reciterId(): number;
  setReciterId(id: number): void;
  /** The reciter list, fetched once; also settles the default reciter. */
  loadReciters(): Promise<Reciter[]>;
  /** The verse's audio file for the current reciter, '' when there is none. */
  verseAudioUrl(verseKey: string): Promise<string>;
  /** A second, quieter player for tapped words, so a tap never interrupts the reciter. */
  tapElement(): HTMLAudioElement;
}

/** The icon markup the reader paints, read live from the design suite's constants. */
export interface ReaderIcons {
  birdMini: string;
  birdFlyer: string;
  tick: string;
  undo: string;
  sliders: string;
  search: string;
  play: string;
  headerMedallion: string;
  bandFrame: string;
  medallion: (page: number) => string;
}

/**
 * Everything the reader needs from the application it lives in. The React
 * reader talks only to this interface, so a different host (My Khatam's
 * bounded portions, the Quran Club app) supplies its own implementation
 * instead of the reader reaching into that host's globals.
 */
export interface ReaderHost {
  t(key: string, vars?: Record<string, string | number>): string;
  /** The live reading preferences; mutations are the host's own state. */
  prefs(): ReadingPreferences;
  normalizePrefs(): void;
  persist(): void;
  syncPrefs(): void;

  currentPage(): number;
  setCurrentPage(page: number): void;
  maxPage(): number;
  versesOn(page: number): PageVerse[];
  /** Builds (and, for the legacy host, registers) the row model for one laid-out verse. */
  verseRow(verse: PageVerse, page: number): VerseRowModel;
  /** Resets whatever per-render verse registry the host keeps, before a page is (re)built. */
  beginPage(): void;
  /**
   * The verse's words as the reader shows them in the current script, one
   * per laid-out location: the page's tajweed glyphs, or the Hafs / IndoPak
   * text. Null when the script has no per-word text (Turkish).
   */
  arabicWords(verse: PageVerse, page: number): string[] | null;
  /** Our Uthmani Hafs text of a laid-out word, for matching Quran.com's word list. */
  hafsText(loc: string): string;
  basmalaText(): string;
  surahTitle(verseKey: string): string;
  juzOf(page: number): number;
  numTranslit(n: number): string;
  surahNameGlyph(surah: number): string;
  icons(): ReaderIcons;

  confirmed(): Set<string>;
  donePages(): Set<number>;
  streakPages(): Set<number>;
  signedIn(): boolean;

  ensureScriptData(): Promise<void>;
  loadPageFont(page: number): Promise<void>;
  reduceMotion(): boolean;

  submitPages(pages: number[]): void;
  prefetchNextPage(): void;
  goToNextPage(button: HTMLElement | null, open: () => void): Promise<void>;
  finishedReading(pages: number[]): void;
  openLanding(): void;
  openReader(): void;
  openMushaf(opts?: LegacyReaderOpenOptions): void;
  startOnboarding(): void;
  tourPageComplete(handle: LegacyReaderHandle): void;

  audio(): RecitationAudio;
  flipPages(screen: HTMLElement): Promise<void>;
  settingsSheet(state: LegacySettingsState): LegacySettingsSheet;
  searchOverlay(screen: HTMLElement): void;
}

const BASMALA_FALLBACK = 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ';

interface LegacyRealAudio {
  el: HTMLAudioElement;
  reciters: Reciter[];
  reciter: number;
}

/** The reader host backed by the not-yet-extracted legacy runtime (app/app.js + app/suite.js). */
export function createLegacyReaderHost(): ReaderHost {
  const byId = () => readLegacyBinding<Record<string, LegacyVerseModel>>('byId');
  const realAudio = () => readLegacyBinding<LegacyRealAudio>('REAL_AUDIO');
  const wordText = (name: 'HAFS' | 'INDO') => readLegacyBinding<Record<string, string> | null>(name);
  let tapAudio: HTMLAudioElement | null = null;
  const audio: RecitationAudio = {
    get element() { return realAudio().el; },
    enabled: () => !window.__NOAUDIO,
    reciterId: () => realAudio().reciter,
    setReciterId(id) {
      realAudio().reciter = id;
      readLegacyBinding<{ set(key: string, value: unknown): void }>('store').set('qc2_reciter', id);
    },
    loadReciters: () => requireLegacy('loadReciters')().then(() => realAudio().reciters),
    verseAudioUrl: (verseKey) => requireLegacy('verseAudioUrl')(verseKey),
    tapElement: () => (tapAudio ??= new Audio()),
  };
  return {
    t: (key, vars) => requireLegacy('t')(key, vars),
    prefs: () => readLegacyBinding<ReadingPreferences>('ui'),
    normalizePrefs() { requireLegacy('normalizeReadingUi')(readLegacyBinding<ReadingPreferences>('ui')); },
    persist: () => requireLegacy('persist')(),
    syncPrefs: () => requireLegacy('queueSettingsSync')(),

    currentPage: () => readLegacyBinding<number>('curPage'),
    setCurrentPage: (page) => writeLegacyBinding('curPage', page),
    maxPage: () => maxMushafPage(),
    versesOn: (page) => requireLegacy('pageVerses')(page),
    verseRow(verse, page) {
      requireLegacy('registerVerse')(verse, page, requireLegacy('glyphsOf')(page));
      return { ...byId()[verse.key], words: window.WORDS?.[verse.key] ?? null };
    },
    beginPage() { window.WORDS = {}; },
    arabicWords(verse, page) {
      const ui = readLegacyBinding<ReadingPreferences>('ui');
      if (ui.script === 'turkish') return null;
      if (ui.script === 'uthmani' && ui.tajweed) {
        const glyphs = requireLegacy('glyphsOf')(page);
        return verse.locs.map((loc) => glyphs[loc] ?? '');
      }
      const src = wordText(ui.script === 'uthmani' ? 'HAFS' : 'INDO');
      return verse.locs.map((loc) => src?.[loc] ?? '');
    },
    hafsText: (loc) => wordText('HAFS')?.[loc] ?? '',
    basmalaText() {
      const qpc = window.QPC_TEXT?.['1:1'];
      return qpc ? qpc.split(' ').slice(0, -1).join(' ') : BASMALA_FALLBACK;
    },
    surahTitle: (verseKey) => requireLegacy('surahTitleOf')(verseKey),
    juzOf: (page) => requireLegacy('juzOf')(page),
    numTranslit: (n) => requireLegacy('numTranslit')(n),
    surahNameGlyph: (surah) => requireLegacy('surahNameGlyph')(surah),
    icons: () => ({
      birdMini: readLegacyBinding<string>('BIRD_MINI'),
      birdFlyer: readLegacyBinding<string>('BIRD_FLYER'),
      tick: readLegacyBinding<string>('TICK'),
      undo: readLegacyBinding<string>('UNDOIC'),
      sliders: readLegacyBinding<string>('SLIDERS'),
      search: readLegacyBinding<string>('SEARCH_IC'),
      play: readLegacyBinding<string>('PLAY'),
      headerMedallion: readLegacyBinding<string>('HEARTH'),
      bandFrame: readLegacyBinding<string>('BAND_FRAME'),
      medallion: readLegacyBinding<(page: number) => string>('MEDAL'),
    }),

    confirmed: () => readLegacyBinding<Set<string>>('confirmedIds'),
    donePages: () => readLegacyBinding<Set<number>>('donePages'),
    streakPages: () => readLegacyBinding<Set<number>>('streakPages'),
    signedIn: () => !!readLegacyBinding<{ account: string | null }>('flow').account,

    ensureScriptData: () => requireLegacy('needScript')(),
    loadPageFont: (page) => requireLegacy('loadV4Font')(page),
    reduceMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,

    submitPages(pages) { void requireLegacy('submitPagesRead')(pages); },
    prefetchNextPage() { void requireLegacy('prefetchNextPage')(); },
    goToNextPage: (button, open) => requireLegacy('goToNextPage')(button, open),
    finishedReading: (pages) => requireLegacy('flowFinishedReading')(pages),
    openLanding: () => requireLegacy('screenLanding')(),
    openReader: () => requireLegacy('openReader')(),
    openMushaf: (opts) => requireLegacy('openMushaf')(opts),
    startOnboarding: () => requireLegacy('startOnboarding')(),
    tourPageComplete: (handle) => requireLegacy('tourPageComplete')(handle),

    audio: () => audio,
    flipPages: (screen) => requireLegacy('flipPages')(screen),
    settingsSheet: (state) => requireLegacy('settingsSheet')(state),
    searchOverlay: (screen) => requireLegacy('searchOverlay')(screen, {}),
  };
}
